import React, { FC, memo, useCallback, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { useProps } from "../../contexts/PropsContext"
import { TurnstileWidget, TurnstileWidgetHandle } from "../auth/TurnstileWidget"
import { NavButton } from "../buttons/NavButton"
import { InputForm } from "../inputs/InputForm"
import { InputFormPassword } from "../inputs/InputFormPassword"

const TURNSTILE_REQUIRED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY

/** Isolate button and widget from input re-renders (autofill, etc.) */
const LoginActions = memo(function LoginActions({
  isLoading,
  turnstileToken,
  onLogin,
  onToken,
  onReset,
  turnstileRef
}: {
  isLoading: boolean
  turnstileToken: string
  onLogin: () => void
  onToken: (token: string) => void
  onReset: () => void
  turnstileRef: React.RefObject<TurnstileWidgetHandle>
}) {
  return (
    <>
      <div className="w-full flex justify-center p-4">
        <NavButton className="w-full" text="Log in" handler={onLogin} disabled={isLoading || (TURNSTILE_REQUIRED && !turnstileToken)} />
      </div>
      <div className="w-full px-4 grid grid-cols-1 gap-4">
        <TurnstileWidget ref={turnstileRef} action="login" onToken={onToken} onReset={onReset} />
      </div>
    </>
  )
})

export const Login: FC = () => {
  const { onUserLogin, onUserLoginTwoFactor, onUserRemoveTwoFactorByRecoveryCode, authenticated } = useProps()
  const { t } = useTranslation("translation", { useSuspense: false })

  const [username, setUsername] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [twoFactorCode, setTwoFactorCode] = useState<string>("")
  const [recoveryCode, setRecoveryCode] = useState<string>("")

  const [loginView, setLoginView] = useState<"default" | "twoFa" | "recoveryCode">("default")
  const [isLoading, setIsLoading] = useState(false)

  // Anti-bot: Cloudflare Turnstile
  const turnstileRef = useRef<TurnstileWidgetHandle>(null)
  const [turnstileToken, setTurnstileToken] = useState<string>("")
  // Ref so onLogin doesn't need turnstileToken in deps — prevents LoginActions re-render on token change
  const turnstileTokenRef = useRef(turnstileToken)
  turnstileTokenRef.current = turnstileToken
  const onTurnstileReset = useCallback(() => setTurnstileToken(""), [])

  const handleLoginViewChange = useCallback((view: "default" | "twoFa" | "recoveryCode") => {
    // Clear fields when switching views
    if (view === "default") {
      setTwoFactorCode("")
      setRecoveryCode("")
    } else if (view === "twoFa") {
      setTwoFactorCode("")
      setRecoveryCode("")
    } else if (view === "recoveryCode") {
      setRecoveryCode("")
      setTwoFactorCode("")
    }
    setLoginView(view)
  }, [])

  const onLogin = useCallback(async (): Promise<void> => {
    if (isLoading || authenticated) return // Prevent double execution

    if (!username) {
      toast.error(t("auth.enterEmailOrUsername") as string)
      return
    }

    if (!password) {
      toast.error(t("auth.enterAPassword") as string)
      return
    }

    setIsLoading(true)
    const result = await onUserLogin(username, password, turnstileTokenRef.current || undefined)
    setIsLoading(false)

    // Anti-bot Layer 3 — reset widget after each attempt (tokens are single-use)
    turnstileRef.current?.reset()
    setTurnstileToken("")

    if (result && "mfa" in result) {
      setUsername("")
      setPassword("")
      setLoginView("twoFa")
      return
    }

    if (result && "role" in result) {
      toast.success(t("auth.successLogin") as string)
      setLoginView("default")
      // Navigation is handled by Home.tsx useEffect
    }
  }, [username, password, onUserLogin, t, isLoading, authenticated])

  const onTwoFaLogin = useCallback(async (): Promise<void> => {
    if (isLoading || authenticated) return // Prevent double execution

    if (!twoFactorCode) {
      toast.error(t("auth.twoFa.enterCode") as string)
      return
    }

    setIsLoading(true)
    const result = await onUserLoginTwoFactor(twoFactorCode)
    setIsLoading(false)

    if (result?.role) {
      toast.success(t("auth.successLogin") as string)
      // Navigation is handled by Home.tsx useEffect
    }
  }, [twoFactorCode, onUserLoginTwoFactor, t, isLoading, authenticated])

  const onRecoveryDisable = useCallback(async (): Promise<void> => {
    if (isLoading) return // Prevent double execution

    if (!recoveryCode) {
      toast.error(t("auth.recovery.enterCode") as string)
      return
    }

    setIsLoading(true)
    const result = await onUserRemoveTwoFactorByRecoveryCode(recoveryCode)
    setIsLoading(false)

    if (result) {
      setRecoveryCode("") // Clear recovery code field
      setLoginView("default")
      toast.success(t("auth.twoFa.disabledSuccessfully") as string)
    }
  }, [recoveryCode, onUserRemoveTwoFactorByRecoveryCode, t, isLoading])

  const onEnter = useCallback(
    (key: string) => {
      if (key === "Enter") {
        onLogin()
      }
    },
    [onLogin]
  )

  const handleFormSubmit = useCallback((e: React.FormEvent, callback: () => void) => {
    e.preventDefault()
    callback()
  }, [])

  const renderLoginForm = useCallback(() => {
    switch (loginView) {
      case "default":
        return (
          <form onSubmit={(e) => handleFormSubmit(e, onLogin)} autoComplete="on">
            <div className="w-full px-4 pt-4 grid grid-cols-1 gap-4">
              <InputForm
                title={t("auth.emailOrUsername")}
                placeholder={t("auth.enterEmailOrUsername")}
                value={username}
                handler={setUsername}
                onEnter={onEnter}
                autoComplete="username"
              />
              <InputFormPassword
                title={t("auth.password")}
                placeholder={t("auth.enterPassword")}
                value={password}
                handler={setPassword}
                onEnter={onEnter}
                autoComplete="current-password"
              />
            </div>
            <LoginActions
              isLoading={isLoading}
              turnstileToken={turnstileToken}
              onLogin={onLogin}
              onToken={setTurnstileToken}
              onReset={onTurnstileReset}
              turnstileRef={turnstileRef}
            />
          </form>
        )
      case "twoFa":
        return (
          <form onSubmit={(e) => handleFormSubmit(e, onTwoFaLogin)} autoComplete="off">
            <div className="w-full px-4 pt-4 grid grid-cols-1 gap-4">
              <InputForm
                key={`twofa-${loginView}`}
                id={"admin-2fa-verification-code"}
                name={"admin-2fa-verification-code"}
                title={t("auth.twoFa.code")}
                placeholder={t("auth.twoFa.enterCode")}
                value={twoFactorCode}
                handler={setTwoFactorCode}
                autoComplete="one-time-code"
              />
              <Link to="#" onClick={() => handleLoginViewChange("recoveryCode")} className="text-14 text-purple underline font-bold">
                {t("auth.recovery.useCode")}
              </Link>
              <Link to="#" onClick={() => handleLoginViewChange("default")} className="text-14 text-purple underline font-bold">
                {t("auth.backToLogin")}
              </Link>
              <div className="w-full flex justify-center p-4">
                <NavButton className="w-full" text={t("auth.login")} handler={onTwoFaLogin} />
              </div>
            </div>
          </form>
        )
      case "recoveryCode":
        return (
          <form onSubmit={(e) => handleFormSubmit(e, onRecoveryDisable)} autoComplete="off">
            <div className="w-full px-4 pt-4 grid grid-cols-1 gap-4">
              <InputForm
                key={`recovery-${loginView}`}
                id={"admin-2fa-recovery-code"}
                name={"admin-2fa-recovery-code"}
                title={t("auth.recovery.code")}
                placeholder={t("auth.recovery.enterCode")}
                value={recoveryCode}
                handler={setRecoveryCode}
                autoComplete="off"
              />
              <Link to="#" onClick={() => handleLoginViewChange("twoFa")} className="text-14 text-purple underline font-bold">
                {t("auth.twoFa.useCode")}
              </Link>
              <Link to="#" onClick={() => handleLoginViewChange("default")} className="text-14 text-purple underline font-bold">
                {t("auth.backToLogin")}
              </Link>
              <div className="w-full flex justify-center p-4">
                <NavButton className="w-full" text={t("auth.login")} handler={onRecoveryDisable} />
              </div>
            </div>
          </form>
        )
      default:
        return null
    }
  }, [
    loginView,
    handleFormSubmit,
    onLogin,
    onTwoFaLogin,
    onRecoveryDisable,
    handleLoginViewChange,
    t,
    username,
    password,
    twoFactorCode,
    recoveryCode,
    isLoading,
    turnstileToken,
    onTurnstileReset
  ])

  return (
    <div className="max-w-full w-[400px] grid grid-cols-1 gap-4 rounded-[8px] border border-[rgba(139,92,246,1)] dark:border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.04)] text-white backdrop-blur-md">
      {renderLoginForm()}
    </div>
  )
}
